import { State } from '@meraki-internal/state';
import { ENTITLEMENTS, IPaymentTerm } from './RevenueCatModel';
import { IFreeTierLimits } from './IOfferMeta';
import { EnvConfiguration } from '../../app/config/EnvConfiguration';
import { AutoPylotPrincipal } from '../../auth/AutoPylotPrincipal';
import { Logger } from '../../support/debug/Logger';
import { CustomerInfo, LogLevel, Offerings, Package, PackageType, Purchases } from '@revenuecat/purchases-js';
import { IRevenueCatAdapter } from './RevenueCatSDKModel';
import { PurchasesErrorAdapter, RevenueCatSDKPurchaseError } from './PurchasesErrorAdapter';
import { eventually } from '../../support/helpers/eventually';

export class RevenueCatWebAdapter extends State<Record<string, never>> implements IRevenueCatAdapter {

    static inject = () => [
        EnvConfiguration,
        AutoPylotPrincipal,
        Logger,
        PurchasesErrorAdapter
    ];
    constructor(
        private config: EnvConfiguration,
        private principal: AutoPylotPrincipal,
        private logger: Logger,
        private errorAdapter: PurchasesErrorAdapter
    ){
        super({});
    }

    private isInitialized = false;
    private lastCustomerInfo?: CustomerInfo;
    private lastOffers?: Offerings;
    private eligibleForTrial = false;

    private fetchSubscriber = async () => {
        const customerInfo = await Purchases.getSharedInstance().getCustomerInfo();
        this.lastCustomerInfo = customerInfo;
        this.setState({ });
    };

    private fetchOffers = async () => {
        this.lastOffers = await Purchases.getSharedInstance().getOfferings();
        this.eligibleForTrial = this.checkTrialEligibility();
        this.setState({ });
    };

    private checkTrialEligibility = () => {
        const availablePackages = this.lastOffers?.current?.availablePackages;
        if (!availablePackages) {
            return false;
        }
        for (const availablePackage of availablePackages) {
            const options = availablePackage.webBillingProduct.subscriptionOptions || {};
            for (const key of Object.keys(options)) {
                const subscriptionOption = options[key];
                if (subscriptionOption.trial) {
                    return true;
                }
            }
        }
        return false;
    };

    private getEntitlements = (): string[] => {
        return Object.keys(this.lastCustomerInfo?.entitlements.active || {});
    };

    private hasUpgraded = () => {
        return this.getEntitlements().includes(ENTITLEMENTS.PLUS);
    };

    private getUpgradePackage = (term: IPaymentTerm) => {
        const packages = this.lastOffers?.current?.availablePackages || [];

        const packageType = term === 'monthly' ? PackageType.Monthly : PackageType.Annual;

        const packageForTerm = packages.find(p => p.packageType === packageType);
        if (!packageForTerm){
            throw new Error(`attempted to find package type ${packageType} for term ${term} but found none. Found "${packages.map(p => p.packageType).join(', ')}"`);
        }
        return packageForTerm;
    };

    private purchasePackage = async (rcPackage: Package) => {
        try {
            this.logger.info(`RevenueCatWebAdapter.purchasePackage-ing`, rcPackage);
            await Purchases.getSharedInstance().purchase({
                rcPackage,
                customerEmail: this.principal.email,
            });
            this.logger.info(`RevenueCatWebAdapter.purchasePackage-ed`, rcPackage);
        }
        catch (err: any) {
            if (!(err as RevenueCatSDKPurchaseError).cancelled) {
                this.logger.info(`RevenueCatWebAdapter.purchasePackage-failed`, { err, jsonError: JSON.stringify(err)});
                throw this.errorAdapter.convert(err);
            }
        }
        finally {
            // revenuecat messes with this
            document.body.style.height = '';
        }
    };

    init = async ({ withRetry }: { withRetry?: boolean } = {}) => {
        this.logger.info('RevenueCatWebAdapter.init-ing');

        const apiKey = this.config.REVENUE_CAT_WEB_PUBLIC_KEY;

        if (!apiKey){
            this.logger.info(`cannot init, no key in config`);
            return;
        }

        try {
            Purchases.setLogLevel(LogLevel.Info);

            if (!Purchases.isConfigured()) {
                Purchases.configure(apiKey, this.principal.userId);
            }

            if (withRetry) {
                await eventually(async () => this.fetchSubscriber(), 10000, 2000);
            } else {
                await this.fetchSubscriber();
            }

            if (withRetry) {
                await eventually(async () => this.fetchOffers(), 10000, 2000);
            } else {
                await this.fetchOffers();
            }

            if (!this.hasUpgradeOffer()){
                this.logger.error(`WARN: user ${this.principal.userId} cannot upgrade b/c we did not get an offer from revenuecat.`);
            }

            this.isInitialized = true;
            this.setState({ });

            this.logger.info('RevenueCatWebAdapter.init-ed', this.getDiagnostics().diagnostics);
        }
        catch (err: any){
            if (!withRetry) {
                // retry without await so app start can proceed
                this.logger.info(`Retrying RevenueCatWebAdapter initialize. Got ${err.toString()} ${JSON.stringify(err)}`);
                this.init({ withRetry: true });
            } else {
                this.logger.error(`Error: RevenueCatWebAdapter tried to initialize and failed. Got ${err.toString()} ${JSON.stringify(err)}`);
            }
        }
    };

    canMakeRealPayment = () => {
        if (!this.isInitialized){
            return false;
        }
        if (!this.hasUpgradeOffer()){
            return false;
        }
        return true;
    };

    upgrade = async (term: IPaymentTerm) => {
        await this.purchasePackage(this.getUpgradePackage(term));
    };

    restore = async () => {
        throw new Error('Restore not supported on web');
    };

    getFreeTierLimits = (): IFreeTierLimits | undefined => {
        const limits: any = this.lastOffers?.current?.metadata?.freeTierLimits;

        if (!limits){
            return undefined;
        }

        // being slightly defensive, b/c this is content managed by product
        // intentonally not adding sensible defaults b/c this shouldn't happen
        for (const key of Object.keys(limits)) {
            if (typeof limits[key] !== 'number') {
                return undefined;
            }
        }

        return limits as any as IFreeTierLimits;
    };

    hasUpgradeOffer = (term: IPaymentTerm = 'monthly') => {
        return Boolean(this.getUpgradePackage(term));
    };

    isEligibleForTrial = () => {
        return this.eligibleForTrial;
    };

    getManagementLink = () => {
        return this.lastCustomerInfo?.managementURL || undefined;
    };

    getDiagnostics = () => {
        let diagnostics: { [label: string]: any } = {
            isInitialized: this.isInitialized,
        };

        if (this.isInitialized){
            diagnostics = {
                ...diagnostics,
                hasUpgraded: this.hasUpgraded(),
                hasUpgradeOffer: this.hasUpgradeOffer(),
                isEligibleForTrial: this.isEligibleForTrial(),
                customerInfo: this.lastCustomerInfo,
                offers: this.lastOffers,
                canMakeRealPayment: this.canMakeRealPayment(),
                freeTierLimits: this.getFreeTierLimits() || '(none)',
            };
        }

        return {
            diagnostics,
            diagnosticsMethods: {
                fetchSubscriber: this.fetchSubscriber,
                fetchOffers: this.fetchOffers,
                purchasePackage: this.purchasePackage
            }
        };
    };
};
